import type { StrictOmit } from '@listening/api-contract/legacy-client-types';
import { Effect, Schema } from 'effect';
import { SchemaUtils } from '@listening/api-contract/schema-utils';
import { optionalCatchingTo } from '@/lib/search-params';
import { Button } from '@listening/frontend/components/radix/button';
import { Input } from '@listening/frontend/components/radix/input';
import {
  type AnyFieldApi,
  createFormHook,
  createFormHookContexts,
} from '@tanstack/react-form';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Eye, EyeClosed } from 'iconoir-react';
import { useEffect, useState } from 'react';
import { Footer } from '@/components/astro/new-design-25/ui/Footer';
import { NavBar } from '@/components/astro/new-design-25/ui/NavBar';
import { GridBackground } from '@/components/shared-ui/grid-background';
import * as m from '@/paraglide/messages';
import { localizeHref } from '@/paraglide/runtime';
import { seo } from '@/seo/seo';
import {
  completePasswordReset,
  requestPasswordReset,
} from '@/services/auth-service';

const { useFieldContext, formContext, fieldContext, useFormContext } =
  createFormHookContexts();
const { useAppForm } = createFormHook({
  fieldContext,
  formContext,
  fieldComponents: {
    TextField,
  },
  formComponents: {
    SubmitButton,
  },
});

const resetSteps = ['request', 'check-email', 'verify', 'success'] as const;

export const Route = createFileRoute('/_marketing/password-reset')({
  validateSearch: Schema.toStandardSchemaV1(
    Schema.Struct({
      // Always resolves to a step, so callers can index `stepToComponent`
      // directly. The two combinators cover different failures:
      // `withDecodingDefaultType` supplies the value when the key is MISSING
      // (which fails at the struct, before any field decoder runs), and
      // `catchDecoding` recovers when the key is present but junk.
      step: Schema.Literals(resetSteps).pipe(
        Schema.catchDecoding(() => Effect.succeedSome('request' as const)),
        Schema.withDecodingDefaultType(Effect.succeed('request' as const)),
      ),
      email: optionalCatchingTo(Schema.String, ''),
      token: Schema.optional(Schema.String),
    }),
  ),
  head: () => ({
    meta: [
      ...seo({
        title: m.password_reset_meta_title(),
        description: m.password_reset_meta_description(),
      }),
    ],
  }),
  component: () => <PasswordResetPage />,
});

type Step = (typeof resetSteps)[number];

const stepToComponent: Record<Step, React.ComponentType> = {
  request: PasswordResetRequest,
  'check-email': PasswordResetCheckEmail,
  verify: PasswordResetVerify,
  success: PasswordResetSuccess,
};

function PasswordResetPage() {
  const { step, token } = Route.useSearch();
  // If a reset token is present (from better-auth email link), jump to verify
  const effectiveStep = token && step === 'request' ? 'verify' : step;
  const Component = stepToComponent[effectiveStep];
  return (
    <>
      <h1 className='sr-only'>{m.password_reset_h1()}</h1>
      <div className='relative'>
        <GridBackground>
          <NavBar />
          <Component />
        </GridBackground>
      </div>
      <Footer />
    </>
  );
}

function PasswordResetRequest() {
  const { email: initialEmail } = Route.useSearch();
  const navigate = useNavigate();

  const form = useAppForm({
    defaultValues: {
      email: initialEmail ?? '',
    },
    validators: {
      onChange: Schema.toStandardSchemaV1(
        Schema.Struct({ email: SchemaUtils.Email }),
      ),
      onSubmitAsync: async ({ value }) => {
        try {
          await requestPasswordReset(value.email);
        } catch {
          return m.password_reset_failed_send();
        }
        await navigate({
          to: '/password-reset',
          search: (prev) => ({
            ...prev,
            step: 'check-email',
            email: value.email,
          }),
        });
      },
    },
  });

  return (
    <div className='relative z-10 mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8'>
      <div className='mx-auto my-10 w-full max-w-[520px] rounded-2xl border border-steel-120 bg-white/80 p-8 shadow-lg backdrop-blur'>
        <h1 className='mt-2 text-center font-sans text-[32px] leading-tight font-bold text-navy-200 md:text-[40px]'>
          {m.password_reset_heading()}
        </h1>
        <form
          className='mx-auto mt-6 flex w-full flex-col items-center space-y-6'
          onSubmit={(e) => {
            e.preventDefault();
            void form.handleSubmit();
          }}
        >
          <form.AppField
            children={(field) => (
              <field.TextField
                className='h-[60px] rounded-lg border-steel-120 bg-mist-220/50 p-4 text-[1rem] leading-tight font-medium'
                label={m.common_email_address()}
                placeholder={m.common_email_address()}
                required
                type='email'
              />
            )}
            name='email'
          />
          <form.AppForm>
            <form.SubmitButton label={m.password_reset_submit()} />
          </form.AppForm>
        </form>
      </div>
    </div>
  );
}

/** Better-auth link flow: user is told to check their email for a reset link. */
function PasswordResetCheckEmail() {
  const { email } = Route.useSearch();

  return (
    <div className='relative z-10 mx-auto max-w-[1296px] px-4 sm:px-6 lg:px-8'>
      <div className='mx-auto my-10 w-full max-w-[520px] rounded-2xl border border-steel-120 bg-white/80 p-8 shadow-lg backdrop-blur'>
        <h1 className='mt-2 text-center font-sans text-[32px] leading-tight font-bold text-navy-200 md:text-[40px]'>
          {m.password_reset_check_email()}
        </h1>
        <p className='mt-4 text-center text-steel-880'>
          {m.password_reset_check_email_sent()}
          {email ? (
            <strong>{email}</strong>
          ) : (
            m.password_reset_check_email_fallback()
          )}
          {m.password_reset_check_email_suffix()}
        </p>
        <div className='mt-6 text-center'>
          <Link
            className='text-sm text-navy-200 underline hover:text-black'
            to={localizeHref('/password-reset')}
            search={{ step: 'request' }}
          >
            {m.password_reset_try_again()}
          </Link>
        </div>
      </div>
    </div>
  );
}

function PasswordResetVerify() {
  const { token } = Route.useSearch();
  if (!token) {
    return <PasswordResetMissingToken />;
  }
  return <PasswordResetWithToken token={token} />;
}

function PasswordResetMissingToken() {
  return (
    <div className='relative z-10 mx-auto max-w-[1296px] px-4 sm:px-6 lg:px-8'>
      <div className='mx-auto my-10 w-full max-w-[520px] rounded-2xl border border-steel-120 bg-white/80 p-8 shadow-lg backdrop-blur'>
        <h1 className='mt-2 text-center font-sans text-[32px] leading-tight font-bold text-navy-200 md:text-[40px]'>
          {m.password_reset_failed_expired()}
        </h1>
        <div className='mt-6 text-center'>
          <Link
            className='text-sm text-navy-200 underline hover:text-black'
            to={localizeHref('/password-reset')}
            search={{ step: 'request' }}
          >
            {m.password_reset_try_again()}
          </Link>
        </div>
      </div>
    </div>
  );
}

function PasswordResetWithToken({ token }: { token: string }) {
  const navigate = useNavigate();

  const form = useAppForm({
    defaultValues: {
      password: '',
    },
    validators: {
      onChange: Schema.toStandardSchemaV1(
        Schema.Struct({
          password: Schema.String.check(
            Schema.isMinLength(8, { message: m.password_reset_password_min() }),
          ),
        }),
      ),
      onSubmitAsync: async ({ value }) => {
        try {
          await completePasswordReset({
            token,
            newPassword: value.password,
          });
        } catch {
          return m.password_reset_failed_expired();
        }
        await navigate({
          to: '/password-reset',
          search: { step: 'success' },
        });
      },
    },
  });

  return (
    <div className='relative z-10 mx-auto max-w-[1296px] px-4 sm:px-6 lg:px-8'>
      <div className='mx-auto my-10 w-full max-w-[520px] rounded-2xl border border-steel-120 bg-white/80 p-8 shadow-lg backdrop-blur'>
        <h1 className='mt-2 text-center font-sans text-[32px] leading-tight font-bold text-navy-200 md:text-[40px]'>
          {m.password_reset_set_new()}
        </h1>
        <form
          className='mx-auto mt-6 flex w-full flex-col items-center space-y-6'
          onSubmit={(e) => {
            e.preventDefault();
            void form.handleSubmit();
          }}
        >
          <form.Field
            name='password'
            children={(field) => (
              <PasswordField
                onBlur={field.handleBlur}
                onChange={field.handleChange}
                value={field.state.value}
              />
            )}
          />
          <form.AppForm>
            <form.SubmitButton label={m.password_reset_submit()} />
          </form.AppForm>
        </form>
      </div>
    </div>
  );
}

function PasswordResetSuccess() {
  useEffect(() => {
    void (async () => {
      if (typeof window === 'undefined') return;
      // dynamic-import -- canvas-confetti touches the DOM at import time and fires once on success, so keep it out of the route bundle.
      const { default: confetti } = await import('canvas-confetti');
      await confetti({
        particleCount: 120,
        spread: 70,
        origin: { y: 0.4 },
        colors: ['#FFD700', '#FFA500', '#FF69B4', '#00FF00', '#87CEEB'],
      });
    })();
  }, []);

  return (
    <div className='relative z-10 mx-auto max-w-[1296px] px-4 sm:px-6 lg:px-8'>
      <div className='mx-auto my-10 w-full max-w-[520px] rounded-2xl border border-steel-120 bg-white/80 p-8 shadow-lg backdrop-blur'>
        <h1 className='mt-2 flex flex-col items-center text-center font-sans text-[32px] leading-tight font-bold text-navy-200 md:text-[40px]'>
          <span className='mb-2 flex'>{m.password_reset_success()}</span>
          <span className='flex'>{m.password_reset_success_message()} 🎉</span>
        </h1>
        <div className='mt-8 flex items-center justify-center'>
          <Link
            className='inline-flex w-full items-center justify-center rounded-full bg-black px-8 py-4 text-center text-base font-medium text-white hover:bg-black/80 sm:w-auto'
            to={localizeHref('/app/login')}
          >
            {m.password_reset_login()}
          </Link>
        </div>
      </div>
    </div>
  );
}

function PasswordField(props: {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
}) {
  const [showPassword, setShowPassword] = useState(false);
  return (
    <div className='relative w-full'>
      <label className='sr-only' htmlFor='password'>
        {m.password_reset_new_password_label()}
      </label>
      <Input
        autoComplete='new-password'
        className='h-[60px] w-full rounded-lg border-steel-120 bg-mist-220/50 p-4 pr-12 text-[1rem] leading-tight font-medium'
        onBlur={props.onBlur}
        onChange={(e) => {
          props.onChange(e.target.value);
        }}
        placeholder={m.password_reset_new_password_placeholder()}
        required
        type={showPassword ? 'text' : 'password'}
        value={props.value}
      />
      <button
        aria-label={
          showPassword ? m.common_hide_password() : m.common_show_password()
        }
        className='absolute top-1/2 right-3 -translate-y-1/2 rounded-full p-1 text-gray-500 hover:bg-black/5'
        onClick={() => {
          setShowPassword((v) => !v);
        }}
        type='button'
      >
        {showPassword ? (
          <EyeClosed className='size-5' />
        ) : (
          <Eye className='size-5' />
        )}
      </button>
    </div>
  );
}

function TextField({
  label,
  ...args
}: {
  label: string;
} & StrictOmit<
  React.ComponentProps<'input'>,
  'value' | 'onChange' | 'onBlur'
>) {
  const field = useFieldContext<string>();
  return (
    <>
      <label className='sr-only' htmlFor={label}>
        {label}
      </label>
      <Input
        {...args}
        onBlur={field.handleBlur}
        onChange={(e) => {
          field.handleChange(e.target.value);
        }}
        value={field.state.value}
      />
      <FieldInfo field={field} />
    </>
  );
}

const isStandardIssue = (e: unknown): e is { message: string } =>
  typeof e === 'object' && e !== null && 'message' in e;

const toErrorMessages = (errors: Array<unknown>) =>
  errors.flatMap((e) =>
    typeof e === 'string' ? [e] : isStandardIssue(e) ? [e.message] : [],
  );

function FieldInfo({ field }: { field: AnyFieldApi }) {
  return (
    !field.state.meta.isValid && (
      <em>{toErrorMessages(field.state.meta.errors).join(',')}</em>
    )
  );
}

function SubmitButton({ label }: { label: string }) {
  const form = useFormContext();
  return (
    <form.Subscribe
      selector={(state) =>
        [
          state.canSubmit,
          state.isSubmitting,
          state.errorMap,
          state.isSubmitSuccessful,
        ] as const
      }
      children={([canSubmit, isSubmitting, errors, isSubmitSuccessful]) => {
        const rootError = errors.onSubmit as string | undefined;
        const message =
          rootError ?? (isSubmitSuccessful ? 'Password reset' : undefined);
        return (
          <>
            <Button
              className={`rounded-full bg-black px-8 py-4 text-white hover:bg-black/80 ${isSubmitting ? 'cursor-not-allowed opacity-60' : ''}`}
              disabled={!canSubmit}
              type='submit'
            >
              {isSubmitting ? m.password_reset_sending() : label}
            </Button>
            {message && (
              <div
                className={`mt-2 text-center text-sm ${rootError ? 'text-red-500' : 'text-green-500'}`}
              >
                {message}
              </div>
            )}
          </>
        );
      }}
    />
  );
}
